import { Download } from "lucide-react";
import { useState } from "react";

import { useExports } from "../lib/export-tray-store.js";
import { buildExportFilters, type ExportFilterState, type ExportFormat } from "../lib/exports.js";
import type { ReportColumn, ReportDefinition } from "../lib/reports.js";
import { Button } from "./ui/button.js";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu.js";

/**
 * The Export control in a report's header: pick a format, and the CURRENT view — date
 * range, filters, drill-down, sort, search, visible columns — is generated as a file in
 * the background. The tray (bottom-right) takes over from here; this button's whole job
 * is to describe the view faithfully at the moment of the click.
 *
 * Renders NOTHING outside an ExportsProvider, so screens mounted alone in tests need no
 * scaffolding for a control they are not testing.
 */
export function ExportButton({
  report,
  from,
  to,
  disabled,
  filterState,
  visibleColumns,
}: {
  readonly report: ReportDefinition;
  readonly from: string;
  readonly to: string;
  /** True while the range is invalid — an export of a broken window helps nobody. */
  readonly disabled: boolean;
  readonly filterState: ExportFilterState;
  readonly visibleColumns: readonly ReportColumn[];
}): React.JSX.Element | null {
  const exports = useExports();
  const [starting, setStarting] = useState(false);
  if (exports === null) return null;

  // A courtesy on top of the server's in-flight cap: the same report already generating
  // is the double-click nobody means.
  const alreadyRunning = exports.jobs.some(
    (job) => job.reportKey === report.key && (job.status === "queued" || job.status === "processing"),
  );

  const start = (format: ExportFormat): void => {
    setStarting(true);
    void exports
      .startExport({
        report,
        format,
        from,
        to,
        filters: buildExportFilters(report, filterState),
        columns: visibleColumns.map((column) => column.key),
      })
      .finally(() => {
        setStarting(false);
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={disabled || starting || alreadyRunning}>
          <Download className="size-4" />
          {alreadyRunning ? "Exporting…" : "Export"}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onSelect={() => {
            start("xlsx");
          }}
        >
          Export as Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            start("csv");
          }}
        >
          Export as CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
